import React, { useEffect, useState } from 'react';
import {
  Page,
  Layout,
  Card,
  Text,
  Button,
  BlockStack,
  InlineStack,
  Badge,
  Banner,
  Box,
} from '@shopify/polaris';
import useConnectionStatus from '../hooks/useConnectionStatus';
import { authenticatedFetch } from '../utils/fetch';

/**
 * Redirect the top-level browser out of the Shopify admin iframe.
 *
 * App Bridge v4 exposes `shopify` globally in an embedded context. Where
 * possible we use its declarative API; otherwise we fall back to setting
 * `window.top.location.href` with a `_top` target.
 */
function redirectTopLevel(url) {
  try {
    if (window.shopify && typeof window.shopify.navigate === 'function') {
      // App Bridge v4: for an absolute URL outside admin, `navigate` will
      // do a full-page redirect.
      window.shopify.navigate(url);
      return;
    }
  } catch (_) {
    // fall through
  }
  try {
    if (window.top && window.top !== window.self) {
      window.top.location.href = url;
      return;
    }
  } catch (_) {
    // cross-origin access denied -- use window.open targeted at _top
  }
  window.open(url, '_top');
}

export default function Settings() {
  const [isConnecting, setIsConnecting] = useState(false);
  const [banner, setBanner] = useState(null); // {tone, message}
  const { isConnected, accountEmail, storeName, loading, refresh } =
    useConnectionStatus();

  // Handle ?apparelhub_connected=1 or ?apparelhub_error=... on page load
  // (set by the /api/apparelhub/callback redirect after OAuth).
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const connected = params.get('apparelhub_connected');
    const error = params.get('apparelhub_error');

    if (connected) {
      setBanner({ tone: 'success', message: 'ApparelHub account connected.' });
      // Clean the URL so refresh doesn't re-show the banner
      window.history.replaceState({}, '', window.location.pathname);
      // Refresh the connection status
      refresh && refresh();
    } else if (error) {
      setBanner({
        tone: 'critical',
        message: `Failed to connect: ${decodeURIComponent(error)}`,
      });
      window.history.replaceState({}, '', window.location.pathname);
    }
    // Run once on mount
    // eslint-disable-next-line
  }, []);

  const handleConnect = async () => {
    setIsConnecting(true);
    setBanner(null);
    try {
      const response = await authenticatedFetch('/api/apparelhub/begin', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
      });
      const data = await response.json();
      if (!response.ok || !data.redirect_url) {
        throw new Error(data.error || data.message || 'Failed to initiate');
      }
      // Full top-level redirect out of the admin iframe.
      redirectTopLevel(data.redirect_url);
    } catch (error) {
      console.error('Failed to start ApparelHub linking:', error);
      setBanner({
        tone: 'critical',
        message: error.message || 'Failed to start linking',
      });
      setIsConnecting(false);
    }
  };

  const handleDisconnect = async () => {
    try {
      await authenticatedFetch('/api/disconnect', { method: 'POST' });
      await refresh();
      setBanner({ tone: 'info', message: 'Disconnected from ApparelHub.' });
    } catch (error) {
      console.error('Failed to disconnect:', error);
      setBanner({
        tone: 'critical',
        message: 'Failed to disconnect',
      });
    }
  };

  return (
    <Page title="Settings" backAction={{ url: '/' }}>
      <Layout>
        <Layout.AnnotatedSection
          title="ApparelHub Account"
          description="Connect your ApparelHub.AI account to enable product syncing and fulfillment management."
        >
          <Card>
            <BlockStack gap="400">
              <InlineStack align="space-between">
                <Text as="h2" variant="headingMd">
                  Connection
                </Text>
                <Badge tone={isConnected ? 'success' : 'attention'}>
                  {loading
                    ? 'Loading...'
                    : isConnected
                    ? 'Connected'
                    : 'Not connected'}
                </Badge>
              </InlineStack>

              {banner && (
                <Banner tone={banner.tone}>
                  <p>{banner.message}</p>
                </Banner>
              )}

              {isConnected ? (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Connected as <strong>{accountEmail}</strong>
                    {storeName ? (
                      <>
                        {' '}to ApparelHub store <strong>{storeName}</strong>
                      </>
                    ) : null}
                    .
                  </Text>
                  <Box>
                    <Button tone="critical" onClick={handleDisconnect}>
                      Disconnect
                    </Button>
                  </Box>
                </BlockStack>
              ) : (
                <BlockStack gap="300">
                  <Text as="p" variant="bodyMd">
                    Connect your ApparelHub account to sync products and fulfill
                    orders. You will be redirected to ApparelHub to approve the
                    connection.
                  </Text>
                  <Box>
                    <Button
                      variant="primary"
                      onClick={handleConnect}
                      loading={isConnecting}
                    >
                      Connect ApparelHub
                    </Button>
                  </Box>
                </BlockStack>
              )}
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>

        <Layout.AnnotatedSection
          title="About"
          description="ApparelHub.AI is an AI-powered print-on-demand merchandise platform."
        >
          <Card>
            <BlockStack gap="200">
              <Text as="p" variant="bodyMd">
                Design custom apparel with AI, manage your product catalog, and
                sell across multiple channels.
              </Text>
              <Box>
                <Button url="https://apparelhub.ai" external>
                  Visit ApparelHub.AI
                </Button>
              </Box>
            </BlockStack>
          </Card>
        </Layout.AnnotatedSection>
      </Layout>
    </Page>
  );
}
